import React from 'react';
import { useScan } from '../contexts/ScanContext';

const SecurityScore = ({ counts: propCounts }) => {
  const { vulnerabilities, calculateSecurityScore, counts: contextCounts } = useScan();

  // Use prop counts if provided, otherwise fall back to context counts or calculate from vulnerabilities
  const counts = propCounts || contextCounts || {
    total: vulnerabilities.length,
    fixed: vulnerabilities.filter(v => v.status === 'fixed').length,
    by_severity: {
      critical: vulnerabilities.filter(v => v.severity === 'critical').length,
      high: vulnerabilities.filter(v => v.severity === 'high').length,
      medium: vulnerabilities.filter(v => v.severity === 'medium').length,
      low: vulnerabilities.filter(v => v.severity === 'low').length,
    }
  };

  const criticalCount = counts?.by_severity?.critical || 0;
  const highCount = counts?.by_severity?.high || 0;
  const mediumCount = counts?.by_severity?.medium || 0;
  const lowCount = counts?.by_severity?.low || 0;
  const fixedCount = counts?.fixed || 0;

  // Calculate the score grade
  const getScoreGrade = () => {
    const score = calculateSecurityScore();
    if (score > 80) return 'A';
    if (score > 60) return 'B';
    if (score > 40) return 'C';
    return 'F';
  };

  // Calculate the border color based on the score
  const getScoreBorderColor = () => {
    const score = calculateSecurityScore();
    if (score > 80) return '#10B981'; // green
    if (score > 60) return '#F59E0B'; // amber
    return '#EF4444'; // red
  };

  // Seven fixed columns left ~49px per card at 375px, which clipped the score
  // to "45/10" and cut the severity labels in half. Below `sm` the score card
  // takes a full row of its own and the five counters tile 3-up.
  return (
    <div className="grid grid-cols-3 gap-3 mb-6 sm:grid-cols-7">
      <div className="bg-teal-50 border border-teal-100 col-span-3 sm:col-span-2 rounded-lg p-3 flex items-center">
        <div
          className="w-10 h-10 rounded-full border-4 flex items-center justify-center mr-3 bg-white"
          style={{ borderColor: getScoreBorderColor() }}
        >
          <span className="text-gray-900 text-xs font-bold">
            {getScoreGrade()}
          </span>
        </div>
        <div>
          <p className="text-xs text-gray-500">Security Score</p>
          <p className="text-xl font-bold text-gray-900">{calculateSecurityScore()}/100</p>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg p-2 flex items-center justify-center shadow-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {criticalCount}
          </div>
          <div className="text-red-600 text-xs font-semibold">Critical</div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg p-2 flex items-center justify-center shadow-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {highCount}
          </div>
          <div className="text-orange-600 text-xs font-semibold">High</div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg p-2 flex items-center justify-center shadow-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {mediumCount}
          </div>
          <div className="text-yellow-600 text-xs font-semibold">Medium</div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg p-2 flex items-center justify-center shadow-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {lowCount}
          </div>
          <div className="text-blue-600 text-xs font-semibold">Low</div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg p-2 flex items-center justify-center shadow-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {fixedCount}
          </div>
          <div className="text-green-600 text-xs font-semibold">Fixed</div>
        </div>
      </div>
    </div>
  );
};

export default SecurityScore;
